import { ChangeEvent, MouseEventHandler, useMemo } from "react";

import { DesignSystemLocale, getText } from "../../Vocab/TranslationManager";
import { FavouriteToggle } from "../FavouriteToggle";
import { NumberBadge } from "../NumberBadge";
import { Tag } from "../Tag/Tag";

import styles from "./MedicalItemCard.module.scss";

import ArchiveRegular from "@/DesignSystem/Atoms/Icons/Types/Archive/archive-regular.svg";
import PaperPlaneTiltRegular from "@/DesignSystem/Atoms/Icons/Types/PaperPlaneTilt/paper-plane-tilt-regular.svg";
import TrayRegular from "@/DesignSystem/Atoms/Icons/Types/Tray/tray-regular.svg";

type MedicalItemCardStatus = "draft" | "received" | "sent" | "none";
type ItemCardReadStatus = "read" | "unread" | "none";
/**
 * Interface representing the properties for the Tag component.
 */
interface IMedicalItemCardProps {
    /**
     * The id of the medical item card
     */
    id: string;

    /**
     * The text to be displayed as the main title of the card.
     */
    title: string;

    /**
     * The date to be displayed on the card in a string format.
     */
    date: string;

    /**
     * An array of names associated with the medical item.
     */
    names?: string[];

    /**
     * A list of descriptions for the medical item
     */
    descriptions?: string[];

    /**
     * Whether the card is currently selected.
     * Optional; defaults to `false`.
     */
    selected?: boolean;

    /**
     * Whether the card is disabled.
     * Optional; defaults to `false`.
     */
    disabled?: boolean;

    /**
     * Callback function to handle click events on the card.
     */
    onClick?: MouseEventHandler<HTMLLIElement>;

    /**
     * The current status of the medical item card.
     */
    status?: MedicalItemCardStatus;

    /**
     * The locale to be used for displaying locale-specific content.
     * Optional.
     */
    locale?: DesignSystemLocale;

    /*
     * The favourite state of the medical item card
     */
    isFavourite?: true | false;

    /*
     * Enable the favourite feature for the medical item card
     */
    enableFavourite?: boolean;

    /*
      * The favourite status button callback function
      */
    onFavouriteButtonClick?: (e: ChangeEvent<HTMLInputElement>) => void;

    /*
     * Will stretch to 100% of the parent's width
     */
    fullWidth?: boolean;

    /**
     * An ID to target in frontend tests.
     */
    dataCy?: string;

    /**
     * The additional content to render on the right bottom in the component.
     */
    children?: React.ReactNode;

    /**
     * The read status of the medical item card.
     */
    readStatus?: ItemCardReadStatus;

    /*
     * The counter for the title in the medical item card.
     */
    titleCounter?: string;
}

function getStatusTag(status: MedicalItemCardStatus, locale: DesignSystemLocale = "en"): JSX.Element {
    switch (status) {
        case "draft":
            return (
                <Tag label={getText(locale, "draft")} variant="warning" size="sm">
                    <ArchiveRegular />
                </Tag>
            );
        case "received":
            return (
                <Tag label={getText(locale, "received")} variant="success" size="sm">
                    <TrayRegular />
                </Tag>
            );
        case "sent":
            return (
                <Tag label={getText(locale, "sent")} variant="brand" size="sm">
                    <PaperPlaneTiltRegular />
                </Tag>
            );
        default:
            return <span />;
    }
}

function MedicalItemCard({
    id,
    title,
    date,
    names = [],
    descriptions = [],
    selected = false,
    disabled = false,
    onClick,
    status = "none",
    locale = "en",
    isFavourite = null,
    enableFavourite = false,
    onFavouriteButtonClick,
    fullWidth,
    dataCy,
    children,
    readStatus = "none",
    titleCounter
}: Readonly<IMedicalItemCardProps>): JSX.Element {
    const statusTag = useMemo(() => getStatusTag(status, locale), [status, locale]);
    const isClickable = !disabled && !!onClick;

    return (
        <li
            id={id}
            className={`${styles.card} ${selected ? styles.selected : ""} ${disabled ? styles.disabled : ""} ${isClickable ? styles.clickable : ""} ${styles.status} ${styles[status] ?? ""} ${styles.readStatus} ${styles[readStatus] ?? ""} ${fullWidth ? styles.fullWidth : ""}`}
            onClick={e => !disabled && onClick?.(e)}
            data-cy={dataCy}
        >
            <div className={styles.statusDateContainer}>
                <span className={styles.statusTag}>{statusTag}</span>
                <label className={styles.date}>{date}</label>
            </div>

            <div className={styles.titleContainer}>
                <label
                    title={title}
                    className={styles.title}
                >
                    {title}
                </label>
                {titleCounter && (
                    <NumberBadge number={titleCounter} variant="neutral" category="outlined" />
                )}
                {enableFavourite && (
                    <div className={styles.favouriteIcon}>
                        <FavouriteToggle isFavourite={isFavourite} onChange={onFavouriteButtonClick} />
                    </div>
                )}
            </div>

            <div className={styles.main}>
                <div className={styles.leftContent}>
                    {names?.length > 0 && (
                        <div className={styles.nameSection}>
                            {names.map((p, i) => <label key={`name_${id}_${i}`} className={styles.names}>{p}</label>)}
                        </div>
                    )}

                    {descriptions?.length > 0 && (
                        <div className={styles.descriptionSection}>
                            {descriptions.map((p, i) => <label key={`description_${id}_${i}`} className={styles.descriptions}>{p}</label>)}
                        </div>
                    )}
                </div>
                {children && (
                    <div className={styles.rightContent}>
                        {children}
                    </div>
                )}
            </div>

        </li>
    );
}

export { MedicalItemCard };
